/**
 * ActiveCallBar / ActiveCallScreen
 *
 * Desktop  — compact card pinned top-right, below the app header.
 * Mobile   — full-screen overlay (like a native phone call).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Mic, MicOff, PhoneOff, PauseCircle, PlayCircle,
  Volume2, VolumeX, ChevronUp, GripHorizontal, ExternalLink,
} from '@/shared/ui/icons';
import { UserAvatar } from '../../shared/ui/user-avatar';
import { cn } from '../../shared/ui/utils';
import { useCall, type CallQuality } from './CallContext';
import { NativeBridge } from '../../services/NativeBridge';
import { useMediaQuery } from '../../hooks/useMediaQuery';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(s: number) {
  return `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;
}

// ─── Quality dot ─────────────────────────────────────────────────────────────

function QualityDot({ quality }: { quality: CallQuality }) {
  if (!quality) return null;
  const color = quality === 'good' ? 'var(--brand)' : quality === 'fair' ? '#f59e0b' : '#ef4444';
  const label = quality === 'good' ? 'Good connection' : quality === 'fair' ? 'Fair connection' : 'Poor connection';
  return <span title={label} className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />;
}

// ─── Speaker selector ─────────────────────────────────────────────────────────

function deviceLabel(d: MediaDeviceInfo) {
  return d.label ? d.label.replace(/\s*\(.*?\)\s*$/, '').trim() : d.deviceId === 'default' ? 'Default' : `Device ${d.deviceId.slice(0, 6)}`;
}

// ─── Dark-mode detector ───────────────────────────────────────────────────────

function useIsDark() {
  const [dark, setDark] = useState(
    () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setDark(document.documentElement.classList.contains('dark')),
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

// ─── Speaker selector ─────────────────────────────────────────────────────────

function SpeakerButton({ compact = false, isDark = true }: { compact?: boolean; isDark?: boolean }) {
  const { outputDevices, outputDeviceId, sinkIdSupported, setOutputDevice } = useCall();
  const isNative = NativeBridge.isRunningNatively();
  const [open, setOpen] = useState(false);

  const nativeRoutes = [
    { id: 'earpiece', label: 'Earpiece' },
    { id: 'speaker',  label: 'Speaker' },
    { id: 'bluetooth', label: 'Bluetooth' },
  ];
  const webDevices = sinkIdSupported ? outputDevices : [];
  if (!isNative && webDevices.length < 2) return null;

  const currentLabel = isNative
    ? (nativeRoutes.find((r) => r.id === outputDeviceId)?.label ?? 'Earpiece')
    : deviceLabel(outputDevices.find((d) => d.deviceId === outputDeviceId) ?? outputDevices[0]);

  const options = isNative
    ? nativeRoutes
    : webDevices.map((d) => ({ id: d.deviceId, label: deviceLabel(d) }));

  if (compact) {
    const isSelected = (id: string) => outputDeviceId === id || (!outputDeviceId && id === 'default');
    const btnBg      = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
    const iconColor  = isDark ? 'white' : '#1c1c1e';
    const labelColor = isDark ? 'rgba(255,255,255,0.7)' : '#8e8e93';
    const dropBg     = isDark ? 'rgba(30,20,40,0.92)' : 'rgba(255,255,255,0.96)';
    const dropBorder = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
    const itemActive = isDark ? 'white' : '#000';
    const itemMuted  = isDark ? 'rgba(255,255,255,0.55)' : '#8e8e93';

    return (
      <div className="relative flex flex-col items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="active:opacity-70 transition-opacity cursor-pointer"
          style={{ width: 64, height: 64, borderRadius: 999, background: btnBg, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}
        >
          <Volume2 style={{ width: 26, height: 26, color: iconColor }} />
        </button>
        <span className="font-apple text-center" style={{ fontSize: 'var(--text-search)', color: labelColor, maxWidth: 80, lineHeight: 1.25 }}>
          {currentLabel}
        </span>

        {open && (
          <div style={{
            position: 'absolute', bottom: 'calc(100% + 12px)', left: '50%', transform: 'translateX(-50%)',
            width: 192, borderRadius: 14, border: `1px solid ${dropBorder}`,
            background: dropBg, backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.22)', padding: '6px 0', zIndex: 10,
          }}>
            {options.map(({ id, label }) => (
              <button key={id} type="button"
                onClick={() => { setOutputDevice(id); setOpen(false); }}
                className="font-apple cursor-pointer transition-opacity active:opacity-60"
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', textAlign: 'left', background: 'none', border: 'none',
                  fontSize: 'var(--text-label)', fontFamily: 'inherit',
                  color: isSelected(id) ? itemActive : itemMuted,
                  fontWeight: isSelected(id) ? 600 : 400,
                }}
              >
                {id === 'earpiece' || id === 'default' ? <VolumeX style={{ width: 14, height: 14, opacity: 0.6 }} /> : <Volume2 style={{ width: 14, height: 14, opacity: 0.6 }} />}
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Desktop compact: small pill
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Audio output"
        className="flex items-center gap-1 h-9 px-2 rounded-full border border-border bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        style={{ fontSize: 'var(--text-search)' }}
      >
        <Volume2 className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-apple max-w-[72px] truncate hidden sm:block">{currentLabel}</span>
        <ChevronUp className={cn('w-3 h-3 flex-shrink-0 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute top-full mt-1.5 right-0 w-48 rounded-xl border border-border bg-card shadow-lg py-1.5 z-10">
          {options.map(({ id, label }) => (
            <button key={id} type="button"
              onClick={() => { setOutputDevice(id); setOpen(false); }}
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 text-left font-apple transition-colors cursor-pointer',
                (outputDeviceId === id || (!outputDeviceId && id === 'default'))
                  ? 'bg-status-brand-bg text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
              style={{ fontSize: 'var(--text-label)' }}
            >
              {id === 'earpiece' || id === 'default' || id === 'communications'
                ? <VolumeX className="w-3.5 h-3.5 opacity-60" />
                : <Volume2 className="w-3.5 h-3.5 opacity-60" />}
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Mobile full-screen call screen ──────────────────────────────────────────

function MobileCallScreen() {
  const {
    status, remoteUser, isMuted, isOnHold, remoteOnHold,
    elapsedSeconds, endCall, toggleMute, toggleHold,
  } = useCall();
  const isDark = useIsDark();

  const displayName = remoteUser?.displayName || remoteUser?.name || '';
  const title       = (remoteUser as any)?.jobTitle || (remoteUser as any)?.position;
  const isConnected = status === 'connected';
  const holdActive  = isOnHold || remoteOnHold;

  const statusText = !isConnected ? 'Calling…'
    : remoteOnHold ? 'On hold'
    : isOnHold     ? `On hold · ${formatDuration(elapsedSeconds)}`
    : formatDuration(elapsedSeconds);

  // ── Theme tokens ─────────────────────────────────────────────────────────────
  const bg          = isDark
    ? 'linear-gradient(160deg, #2d1246 0%, #1a0a2e 60%, #0f0620 100%)'
    : 'linear-gradient(180deg, #f2f2f7 0%, #ffffff 60%)';
  const textPrimary   = isDark ? '#ffffff' : '#000000';
  const textSecondary = isDark ? 'rgba(255,255,255,0.55)' : '#8e8e93';
  const btnBg         = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)';
  const btnColor      = isDark ? 'white' : '#1c1c1e';
  const labelColor    = isDark ? 'rgba(255,255,255,0.7)' : '#8e8e93';

  const numCols = isConnected ? 3 : 2;

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col items-center"
      style={{
        background: bg,
        paddingTop:    'calc(env(safe-area-inset-top, 0px) + 52px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 48px)',
        paddingLeft: 24, paddingRight: 24,
      }}
    >
      {/* Top status label */}
      <p className="font-apple" style={{ fontSize: 'var(--text-label)', color: textSecondary }}>
        {holdActive ? '⏸ On Hold' : isConnected ? 'Connected' : 'Calling…'}
      </p>

      <div className="flex-1" />

      {/* Avatar + identity */}
      <div className="flex flex-col items-center gap-5">
        <div className="relative">
          {!isConnected && (
            <span className="absolute inset-0 rounded-full animate-ping opacity-20"
              style={{ background: 'var(--brand)' }} />
          )}
          <UserAvatar
            src={remoteUser?.avatar}
            name={displayName}
            size={100}
            border={`3px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)'}`}
            fallbackBg={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'}
            fallbackColor={isDark ? 'white' : '#1c1c1e'}
          />
        </div>
        <div className="text-center">
          <p className="font-apple font-semibold" style={{ fontSize: '1.5rem', color: textPrimary }}>
            {displayName}
          </p>
          {title && (
            <p className="font-apple mt-0.5" style={{ fontSize: 'var(--text-label)', color: textSecondary }}>
              {title}
            </p>
          )}
          <p className="font-apple mt-1 tabular-nums" style={{ fontSize: 'var(--text-menu)', color: textSecondary }}>
            {statusText}
          </p>
        </div>
      </div>

      <div className="flex-1" />

      {/* Controls */}
      <div className="w-full flex flex-col items-center gap-10">
        {/* Secondary controls — CSS grid for equal columns */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${numCols}, 1fr)`,
          gap: '0 8px',
          width: '100%',
          maxWidth: 280,
        }}>
          <SpeakerButton compact isDark={isDark} />

          {isConnected && (
            <div className="flex flex-col items-center gap-1.5">
              <button
                type="button"
                onClick={toggleHold}
                disabled={remoteOnHold}
                className="active:opacity-70 transition-opacity"
                style={{
                  width: 64, height: 64, borderRadius: 999,
                  background: isOnHold ? (isDark ? 'rgba(245,158,11,0.28)' : 'rgba(245,158,11,0.15)') : btnBg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: 'none', cursor: remoteOnHold ? 'not-allowed' : 'pointer',
                  opacity: remoteOnHold ? 0.4 : 1,
                }}
              >
                {isOnHold
                  ? <PlayCircle style={{ width: 26, height: 26, color: 'var(--status-warning)' }} />
                  : <PauseCircle style={{ width: 26, height: 26, color: btnColor }} />
                }
              </button>
              <span className="font-apple" style={{ fontSize: 'var(--text-search)', color: labelColor }}>
                {isOnHold ? 'Resume' : 'Hold'}
              </span>
            </div>
          )}

          <div className="flex flex-col items-center gap-1.5">
            <button
              type="button"
              onClick={toggleMute}
              className="active:opacity-70 transition-opacity cursor-pointer"
              style={{
                width: 64, height: 64, borderRadius: 999,
                background: isMuted
                  ? (isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.15)')
                  : btnBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: 'none',
              }}
            >
              {isMuted
                ? <MicOff style={{ width: 26, height: 26, color: btnColor }} />
                : <Mic    style={{ width: 26, height: 26, color: btnColor }} />
              }
            </button>
            <span className="font-apple" style={{ fontSize: 'var(--text-search)', color: labelColor }}>
              {isMuted ? 'Unmute' : 'Mute'}
            </span>
          </div>
        </div>

        {/* End call */}
        <button
          type="button"
          onClick={endCall}
          className="active:opacity-80 transition-opacity cursor-pointer"
          style={{
            width: 64, height: 64, borderRadius: 999,
            background: 'var(--destructive)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 'none',
            boxShadow: '0 4px 20px color-mix(in srgb, var(--destructive) 45%, transparent)',
          }}
        >
          <PhoneOff style={{ width: 26, height: 26, color: 'var(--destructive-foreground)' }} />
        </button>
      </div>
    </div>
  );
}

// ─── Desktop call card content (renders in both docked and pip contexts) ────────

function CallCardContent({ inPip = false }: { inPip?: boolean }) {
  const {
    status, remoteUser, isMuted, isOnHold, remoteOnHold,
    elapsedSeconds, quality, endCall, toggleMute, toggleHold,
  } = useCall();

  const displayName = remoteUser?.displayName || remoteUser?.name || '';
  const isConnected = status === 'connected';
  const holdActive  = isOnHold || remoteOnHold;

  const statusText = !isConnected ? 'Calling…'
    : remoteOnHold ? 'On hold'
    : isOnHold     ? `On hold · ${formatDuration(elapsedSeconds)}`
    : formatDuration(elapsedSeconds);

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border bg-card shadow-xl overflow-hidden transition-colors w-72',
        holdActive ? 'border-amber-400/40' : 'border-border',
      )}
      style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.22)' }}
    >
      {/* Thin accent bar */}
      <div className="h-0.5 w-full" style={{ background: holdActive ? '#f59e0b' : 'var(--brand)' }} />

      {/* Drag handle + identity row */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-2">
        {/* Drag grip — only shown when docked (pip window has native drag) */}
        {!inPip && (
          <GripHorizontal
            className="w-4 h-4 flex-shrink-0 text-muted-foreground opacity-40 cursor-grab active:cursor-grabbing"
            data-drag-handle
          />
        )}

        <UserAvatar
          src={remoteUser?.avatar}
          name={displayName}
          size={36}
          className="flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          <p className="font-semibold font-apple text-foreground truncate leading-tight" style={{ fontSize: 'var(--text-label)' }}>
            {displayName}
          </p>
          <div className="flex items-center gap-1.5">
            {isConnected && !holdActive
              ? <QualityDot quality={quality} />
              : <span className={cn('w-1.5 h-1.5 rounded-full', !isConnected && 'animate-pulse')}
                  style={{ background: holdActive ? '#f59e0b' : 'var(--muted-foreground)', flexShrink: 0 }} />
            }
            <p className="font-apple leading-tight tabular-nums"
              style={{ fontSize: 'var(--text-search)', color: holdActive ? '#f59e0b' : 'var(--muted-foreground)' }}>
              {statusText}
            </p>
          </div>
        </div>

        {/* End call */}
        <button
          type="button"
          onClick={endCall}
          title="End call"
          className="flex items-center justify-center w-9 h-9 rounded-full bg-destructive text-white transition-opacity hover:opacity-80 cursor-pointer flex-shrink-0"
        >
          <PhoneOff className="w-4 h-4" />
        </button>
      </div>

      {/* Controls row */}
      {isConnected && (
        <div className="flex items-center gap-2 px-4 pb-3" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="flex items-center gap-1.5 pt-2 flex-1">
            <SpeakerButton />

            <button
              type="button"
              onClick={toggleHold}
              disabled={remoteOnHold}
              title={isOnHold ? 'Resume' : 'Hold'}
              className={cn(
                'flex items-center justify-center w-9 h-9 rounded-full border transition-colors cursor-pointer',
                isOnHold
                  ? 'border-amber-400 bg-amber-50 text-amber-600 dark:bg-amber-950'
                  : 'border-border bg-muted text-muted-foreground hover:text-foreground',
                remoteOnHold && 'opacity-40 cursor-not-allowed',
              )}
            >
              {isOnHold ? <PlayCircle className="w-4 h-4" /> : <PauseCircle className="w-4 h-4" />}
            </button>

            {!isOnHold && (
              <button
                type="button"
                onClick={toggleMute}
                title={isMuted ? 'Unmute' : 'Mute'}
                className={cn(
                  'flex items-center justify-center w-9 h-9 rounded-full border transition-colors cursor-pointer',
                  isMuted
                    ? 'border-brand bg-status-brand-bg text-foreground'
                    : 'border-border bg-muted text-muted-foreground hover:text-foreground',
                )}
              >
                {isMuted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Desktop floating draggable card ─────────────────────────────────────────

const PIP_SUPPORTED = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

function DesktopCallCard() {
  // ── position / drag ──────────────────────────────────────────────────────
  const [pos, setPos] = useState(() => ({
    x: window.innerWidth - 304,  // 288px card + 16px margin
    y: (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--qs-web-header-height') || '64') || 64) + 8,
  }));
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Only drag from the grip handle or the top bar (not buttons)
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[data-no-drag]')) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: pos.x, startPosY: pos.y };
  }, [pos]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    const cardW = cardRef.current?.offsetWidth ?? 288;
    const cardH = cardRef.current?.offsetHeight ?? 160;
    setPos({
      x: Math.max(0, Math.min(window.innerWidth  - cardW, dragRef.current.startPosX + dx)),
      y: Math.max(0, Math.min(window.innerHeight - cardH, dragRef.current.startPosY + dy)),
    });
  }, []);

  const onPointerUp = useCallback(() => { dragRef.current = null; }, []);

  // ── picture-in-picture ───────────────────────────────────────────────────
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [pipContainer, setPipContainer] = useState<HTMLElement | null>(null);

  const openPip = useCallback(async () => {
    if (!PIP_SUPPORTED) return;
    try {
      const pip = await (window as any).documentPictureInPicture.requestWindow({ width: 304, height: 160 });
      // Copy all styles so Tailwind + CSS variables work in the pip window
      [...document.styleSheets].forEach(sheet => {
        try {
          const rules = [...(sheet.cssRules ?? [])].map(r => r.cssText).join('');
          const style = pip.document.createElement('style');
          style.textContent = rules;
          pip.document.head.appendChild(style);
        } catch {
          if (sheet.href) {
            const link = pip.document.createElement('link');
            link.rel = 'stylesheet'; link.href = sheet.href;
            pip.document.head.appendChild(link);
          }
        }
      });
      // Match the host page's color scheme class (dark / light)
      pip.document.documentElement.className = document.documentElement.className;
      const container = pip.document.createElement('div');
      container.style.cssText = 'margin:8px; font-family: inherit;';
      pip.document.body.appendChild(container);
      pip.addEventListener('pagehide', () => { setPipWindow(null); setPipContainer(null); });
      setPipWindow(pip);
      setPipContainer(container);
    } catch {
      // User cancelled or not supported — ignore
    }
  }, []);

  // Keep pip color scheme in sync with the host
  useEffect(() => {
    if (!pipWindow) return;
    const observer = new MutationObserver(() => {
      pipWindow.document.documentElement.className = document.documentElement.className;
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [pipWindow]);

  // ── render ───────────────────────────────────────────────────────────────
  if (pipWindow && pipContainer) {
    return createPortal(<CallCardContent inPip />, pipContainer);
  }

  return (
    <div
      ref={cardRef}
      className="fixed z-[150] pointer-events-auto select-none"
      style={{ left: pos.x, top: pos.y, cursor: dragRef.current ? 'grabbing' : 'grab' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    >
      {/* Pop-out button — top-left corner of the card wrapper */}
      {PIP_SUPPORTED && (
        <button
          type="button"
          data-no-drag
          onClick={openPip}
          title="Pop out to separate window"
          className="absolute -top-2 -left-2 z-10 flex items-center justify-center w-6 h-6 rounded-full bg-card border border-border shadow text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          style={{ pointerEvents: 'auto' }}
        >
          <ExternalLink className="w-3 h-3" />
        </button>
      )}
      <CallCardContent />
    </div>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export function ActiveCallBar() {
  const { status } = useCall();
  // 820px = iPad Air portrait. Intentional: portrait tablets get the
  // full-screen call UI alongside phones (deliberately wider than the
  // canonical <1024 mobile cut). Landscape tablets and desktops keep
  // the compact bottom bar so they don't lose context on a single tap.
  const isMobile = useMediaQuery('(max-width: 820px)');

  if (status !== 'outgoing' && status !== 'connected') return null;

  return isMobile ? <MobileCallScreen /> : <DesktopCallCard />;
}
